import React, { useEffect, useMemo, useState } from "react";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { adminApi } from "../../lib/portalApi";
import { toast } from "sonner";
import { Save, FileEdit } from "lucide-react";

export default function CmsTab() {
  const [blocks, setBlocks] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [activePage, setActivePage] = useState(null);

  const load = async () => {
    const r = await adminApi.cmsList();
    setBlocks(r.blocks || []);
    const d = {};
    (r.blocks || []).forEach(b => { d[`${b.page_key}::${b.slot}`] = b.value || ""; });
    setDrafts(d);
  };
  useEffect(() => { load(); }, []);

  const grouped = useMemo(() => {
    const m = {};
    blocks.forEach(b => {
      m[b.page_key] = m[b.page_key] || [];
      m[b.page_key].push(b);
    });
    Object.values(m).forEach(arr => arr.sort((a, b) => a.slot.localeCompare(b.slot)));
    return m;
  }, [blocks]);

  const pages = Object.keys(grouped).sort();
  useEffect(() => { if (!activePage && pages.length) setActivePage(pages[0]); }, [pages, activePage]);

  const save = async (b) => {
    const key = `${b.page_key}::${b.slot}`;
    try {
      await adminApi.cmsPut(b.page_key, b.slot, drafts[key] ?? "");
      toast.success(`Saved ${b.page_key}/${b.slot}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  return (
    <div className="space-y-4" data-testid="admin-cms-tab">
      <div className="flex gap-2 flex-wrap">
        {pages.map(p => (
          <button key={p} onClick={() => setActivePage(p)}
            className={`text-[11px] uppercase tracking-[0.18em] px-3 py-1.5 rounded border transition-colors ${
              activePage === p
                ? "bg-navy-soft text-gold border-gold"
                : "text-cream-dim border-gold-dim/30 hover:text-gold"
            }`}
            data-testid={`cms-page-${p}`}>
            {p}
          </button>
        ))}
      </div>

      {activePage && (
        <div className="space-y-3">
          {(grouped[activePage] || []).map(b => {
            const key = `${b.page_key}::${b.slot}`;
            return (
              <div key={key} className="glass p-5" data-testid={`cms-block-${b.slot}`}>
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-steel">
                      {b.page_key} · <span className="text-gold">{b.slot}</span>
                    </div>
                    {b.label && <div className="text-cream text-sm mt-0.5">{b.label}</div>}
                  </div>
                  <FileEdit size={14} className="text-electric" />
                </div>
                <Textarea
                  rows={4}
                  value={drafts[key] ?? ""}
                  onChange={e => setDrafts({ ...drafts, [key]: e.target.value })}
                  className="bg-navy-deep border-gold-dim/40 text-cream font-mono text-sm"
                  data-testid={`cms-textarea-${b.slot}`}
                />
                <div className="mt-3 flex justify-end">
                  <Button onClick={() => save(b)} className="btn-gold h-9 px-4 text-xs"
                    data-testid={`cms-save-${b.slot}`}>
                    <Save size={12} className="mr-1.5" /> Save
                  </Button>
                </div>
              </div>
            );
          })}
          {!(grouped[activePage] || []).length && (
            <div className="glass p-8 text-center text-cream-dim italic">No content blocks seeded for this page.</div>
          )}
        </div>
      )}
    </div>
  );
}
